<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useBookingOptionStore } from '@/@vxapp/Products/BookingOptions/useBookingOptionStore'
import BookingOptionDialog from '@/components/Dialogs/BookingOptions/BookingOptionDialog.vue'
import BookingOptionListToolbar from '@/components/ServiceLists/BookingOptions/BookingOptionListToolbar.vue'
import BookingOptionsDatatable from '@/components/Tables/BookingOptions/BookingOptionsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type BookingOptionData = Record<string, unknown>
type DialogDisplayTypeValue = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({
  meta: { ...PageMeta.BookingOptions.List.Meta, pageTitle: PageMeta.BookingOptions.List.Title },
})

const log = createLogger('BookingOptionsListPage')
const route = useRoute()
const router = useRouter()
const bookingOptionStore = useBookingOptionStore() as any

const bookingOptionsDataTable =
  useTemplateRef<InstanceType<typeof BookingOptionsDatatable>>('bookingOptionsDataTable')

const bookingOptionData = ref<BookingOptionData | null>(null)
const dialogDisplayType = shallowRef<DialogDisplayTypeValue>(DialogDisplayType.CREATE)
const isBookingOptionDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)
const defaultQueryParams: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']
const sortableFields = new Set(['id', 'category', 'optionName'])
const limitItems = [10, 25, 50, 100]

function normalizeQueryValue(value: unknown): string | undefined {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  if (typeof normalizedValue === 'number') return String(normalizedValue)

  return typeof normalizedValue === 'string' ? normalizedValue : undefined
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeSort(value: unknown) {
  const sort = normalizeQueryValue(value)
  const sortField = sort?.replace(/^[+-]/, '')

  return sort && sortField && sortableFields.has(sortField) && /^[+-]/.test(sort)
    ? sort
    : defaultQueryParams.sort
}

function normalizeQueryParams(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = normalizePositiveInteger(source.limit, defaultQueryParams.limit)
  const search = normalizeQueryValue(source.search)?.trim()

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source.page, defaultQueryParams.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaultQueryParams.limit,
    sort: normalizeSort(source.sort),
    search: search || undefined,
  }
}

const queryParams = computed<PaginationQueryParams>(() =>
  normalizeQueryParams(bookingOptionStore.queryParams ?? defaultQueryParams)
)

function queryParamsMatch(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRouteQuery() {
  const currentQuery = normalizeQueryParams(bookingOptionStore.queryParams ?? defaultQueryParams)

  const routeQuery = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  bookingOptionStore.queryParams = routeQuery

  return true
}

function buildRouteQuery(
  source: Record<string, unknown> = bookingOptionStore.queryParams ?? defaultQueryParams
) {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

async function fetchBookingOptions() {
  await bookingOptionsDataTable.value?.fetchBookingOptions()
}

async function updateQueryParams(patch: Partial<PaginationQueryParams>) {
  const nextQueryParams = normalizeQueryParams({ ...bookingOptionStore.queryParams, ...patch })

  bookingOptionStore.queryParams = nextQueryParams

  const nextRouteQuery = buildRouteQuery({ ...nextQueryParams })

  if (JSON.stringify(route.query) !== JSON.stringify(nextRouteQuery))
    await router.replace({ query: nextRouteQuery })

  await nextTick()
  await fetchBookingOptions()
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleClear() {
  bookingOptionStore.queryParams = { ...defaultQueryParams }

  const nextRouteQuery = buildRouteQuery({ ...defaultQueryParams })
  if (JSON.stringify(route.query) !== JSON.stringify(nextRouteQuery))
    await router.replace({ query: nextRouteQuery })

  await nextTick()
  await fetchBookingOptions()
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit, page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

function handleLoaded(entriesFound: number) {
  resultCount.value = entriesFound
}

async function handleCreateBookingOption() {
  try {
    const { responseCode } = await bookingOptionStore.fetchTemplate()

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load booking option template', { responseCode })

      return
    }

    bookingOptionData.value = cloneReactiveData(bookingOptionStore.template ?? {})
    dialogDisplayType.value = DialogDisplayType.CREATE
    isBookingOptionDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load booking option template', error)
  }
}

async function openBookingOptionDialog(optionGUID: string, displayType: DialogDisplayTypeValue) {
  try {
    const { responseCode } = await bookingOptionStore.fetch(optionGUID)

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load booking option', { optionGUID, responseCode })

      return
    }

    bookingOptionData.value = cloneReactiveData(bookingOptionStore.bookingOption ?? {})
    dialogDisplayType.value = displayType
    isBookingOptionDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load booking option', error)
  }
}

async function handleEditBookingOption(optionGUID: string) {
  await openBookingOptionDialog(optionGUID, DialogDisplayType.EDIT)
}

async function handleViewBookingOption(optionGUID: string) {
  await openBookingOptionDialog(optionGUID, DialogDisplayType.VIEW)
}

async function handleSaveBookingOption(newBookingOptionData: BookingOptionData) {
  if (dialogDisplayType.value === DialogDisplayType.VIEW) return

  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await bookingOptionStore.create(newBookingOptionData, false)
        : await bookingOptionStore.update(newBookingOptionData, false)

    const expectedCode =
      dialogDisplayType.value === DialogDisplayType.CREATE ? ResponseCode.CREATED : ResponseCode.OK

    if (response.responseCode !== expectedCode) {
      log.error('Failed to save booking option', { responseCode: response.responseCode })

      return
    }

    await fetchBookingOptions()
  } catch (error) {
    log.error('Failed to save booking option', error)
  }
}

bookingOptionStore.queryParams = { ...defaultQueryParams }
applyRouteQuery()
isRouteQueryReady.value = true

onMounted(fetchBookingOptions)

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return

    await nextTick()
    await fetchBookingOptions()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="booking-options-workspace"
    data-testid="booking-options-workspace"
  >
    <VCard class="booking-options-workspace__hero">
      <VCardItem>
        <div class="booking-options-workspace__hero-content">
          <div>
            <span class="booking-options-workspace__eyebrow">Booking configuration</span>
            <VCardTitle class="booking-options-workspace__title"
              >Product booking options</VCardTitle
            >
            <VCardSubtitle class="booking-options-workspace__subtitle">
              Shape reusable booking properties, inspect their categories, and refine every option.
            </VCardSubtitle>
          </div>
          <div class="booking-options-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>

      <VDivider />

      <VCardActions class="booking-options-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateBookingOption"
        >
          New booking option
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchBookingOptions"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <BookingOptionListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="bookingOptionStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="fetchBookingOptions"
    />

    <BookingOptionsDatatable
      ref="bookingOptionsDataTable"
      @edit="handleEditBookingOption"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
      @view="handleViewBookingOption"
    />
  </main>

  <BookingOptionDialog
    v-if="bookingOptionData"
    v-model:is-dialog-visible="isBookingOptionDialogVisible"
    :display-type="dialogDisplayType"
    :booking-option-data="bookingOptionData"
    @create="handleSaveBookingOption"
    @update="handleSaveBookingOption"
  />
</template>

<style scoped lang="scss">
.booking-options-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.booking-options-workspace__hero {
  overflow: hidden;
  min-inline-size: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.booking-options-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.booking-options-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.booking-options-workspace__title {
  overflow: visible;
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
  text-overflow: clip;
  white-space: normal;
}

.booking-options-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.booking-options-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.booking-options-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.booking-options-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.5rem;
  font-weight: 590;
  letter-spacing: -0.04em;
}

.booking-options-workspace__actions {
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.booking-options-workspace__actions :deep(.v-btn) {
  min-block-size: 2.5rem;
  border-radius: 6px;
}

@media (max-width: 599px) {
  .booking-options-workspace {
    gap: 1rem;
  }

  .booking-options-workspace__hero-content {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }

  .booking-options-workspace__metric {
    inline-size: 100%;
  }

  .booking-options-workspace__actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    padding: 0.75rem;
  }

  .booking-options-workspace__actions :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
